// src/admin/screens/admins/modals/DeactivateAdminModal.tsx
// Flips admin_users.is_active to false. The target admin can no longer
// sign in (AuthContext rejects inactive admins in its admin-row check).
// Existing sessions are invalidated on next JWT refresh.
//
// Re-activation is intentionally NOT available in v1 — it's a rare,
// high-trust event and going via database admin (is_active=true) keeps
// the bar high. Could add an `admin.reactivate` action in a later sprint.
import { useState, type FormEvent } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { supabase } from '@admin/lib/supabase';
import { recordAudit } from '@admin/lib/audit';
import Modal from '@admin/components/Modal';
import Button from '@admin/components/Button';
import FormField from '@admin/components/FormField';
import { useToast } from '@admin/components/ToastProvider';
import { translateError } from '@admin/utils/translateError';
import type { AdminRow } from '@admin/hooks/useAdmins';
import styles from './AdminModals.module.css';

interface Props {
  admin: AdminRow;
  onClose: (refresh: boolean) => void;
}

export default function DeactivateAdminModal({ admin, onClose }: Props) {
  const toast = useToast();
  const qc = useQueryClient();
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (reason.trim().length < 10) {
      toast({ kind: 'error', msg: 'Reason must be at least 10 characters.' });
      return;
    }
    setBusy(true);
    try {
      const { error } = await supabase
        .from('admin_users')
        .update({ is_active: false })
        .eq('id', admin.id);
      if (error) throw error;
      await recordAudit({
        action: 'admin.deactivate',
        target_type: 'admin',
        target_id: admin.id,
        payload: { reason: reason.trim() },
      });
      toast({
        kind: 'success',
        msg: 'Admin deactivated. They can no longer sign in.',
      });
      await qc.invalidateQueries({ queryKey: ['admin', admin.id] });
      await qc.invalidateQueries({ queryKey: ['admins'] });
      onClose(true);
    } catch (err) {
      toast({ kind: 'error', msg: translateError(err as Error) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open
      onClose={() => onClose(false)}
      title={`Deactivate ${admin.display_name}`}
    >
      <form onSubmit={submit} className={styles.form}>
        <p className={styles.lead}>
          Existing sessions are invalidated on next JWT refresh. Re-activation
          is not available from the console in v1 — a database admin must flip{' '}
          <code>is_active = true</code> by hand.
        </p>
        <FormField
          label="Reason"
          htmlFor="da-reason"
          hint="Required and audit-logged."
          required
        >
          <textarea
            id="da-reason"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </FormField>
        <div className={styles.actions}>
          <Button
            type="button"
            variant="secondary"
            onClick={() => onClose(false)}
            disabled={busy}
          >
            Cancel
          </Button>
          <Button type="submit" variant="danger" loading={busy}>
            Deactivate
          </Button>
        </div>
      </form>
    </Modal>
  );
}
